import {
  For,
  Show,
  createMemo,
  createSignal,
} from "solid-js";
import { A, useNavigate, useSearchParams } from "@solidjs/router";
import { api } from "../api/client";
import type { CompletedWorkout, Exercise, PlannedWorkout } from "../api/types";
import { rowType, summarizeExercise } from "../lib/activity";
import { t, locale } from "../i18n";
import { createCachedResource } from "../lib/cache";
import { displayPerformedAt } from "../lib/datetime";
import { formatDuration } from "../lib/duration";
import { withFrom } from "../lib/nav";
import { repeatCompletedHref } from "../lib/repeatWorkout";
import PageHero from "../components/PageHero";

type Tab = "all" | "completed" | "planned";

type ListItem =
  | { kind: "completed"; sortAt: number; dateLabel: string; w: CompletedWorkout }
  | { kind: "planned"; sortAt: number; dateLabel: string; w: PlannedWorkout };

function paramStr(
  raw: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(raw)) return raw[0];
  return raw;
}

function parseTab(raw: string | string[] | undefined): Tab {
  const v = paramStr(raw);
  if (v === "completed" || v === "planned" || v === "all") return v;
  return "all";
}

function dateKeyISO(isoOrDate: string): string {
  // plannedFor is YYYY-MM-DD; performedAt is full ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return isoOrDate.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function formatListDate(isoOrDate: string): string {
  const key = dateKeyISO(isoOrDate);
  const d = new Date(`${key}T12:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  const loc = locale().startsWith("en") ? "en-US" : "ru-RU";
  return d.toLocaleDateString(loc, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Preview lines under a workout: names when every exercise is strength (as
 * before), otherwise one "Name: summary" line per exercise plus "ещё N: names".
 */
function workoutPreview(exercises: Exercise[], max = 2): string[] {
  const named = exercises.filter((e) => e.name.trim());
  if (named.every((e) => rowType(e) === "strength")) {
    const line = exercisePreview(named.map((e) => e.name));
    return line ? [line] : [];
  }
  const lines = named.slice(0, max).map((e) => summarizeExercise(e));
  const rest = named.slice(max);
  if (rest.length) {
    lines.push(
      `${t("MORE_COUNT").replace("{n}", String(rest.length))}: ${rest
        .map((e) => e.name.trim())
        .join(", ")}`,
    );
  }
  return lines;
}

function exercisePreview(names: string[], max = 3): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (clean.length === 0) return "";
  const head = clean.slice(0, max);
  const more = clean.length - head.length;
  return more > 0 ? `${head.join(" · ")} +${more}` : head.join(" · ");
}

export default function WorkoutsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tab = () => parseTab(search.tab);
  const dateFilter = () => {
    const d = paramStr(search.date);
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  };

  // With ?date=… only fetch that day (±1 day slack for finishedAt vs performedAt);
  // the memo below still filters exactly. Without a filter keep full history.
  const dayRange = (d: string) => {
    const base = Date.parse(`${d}T00:00:00Z`);
    const day = 86_400_000;
    return {
      from: new Date(base - day).toISOString(),
      to: new Date(base + 2 * day).toISOString(),
    };
  };
  const [completed] = createCachedResource(() => ({ d: dateFilter() }), {
    key: ({ d }) => `completed:${d ?? "all"}`,
    fetch: ({ d }) =>
      d ? api.listCompleted(dayRange(d).from, dayRange(d).to) : api.listCompleted(),
  });
  const [planned] = createCachedResource(() => ({ d: dateFilter() }), {
    key: ({ d }) => `planned:${d ?? "all"}`,
    fetch: ({ d }) => (d ? api.listPlanned(d, d) : api.listPlanned()),
  });

  const loading = () => completed.loading || planned.loading;

  const items = createMemo((): ListItem[] => {
    const filterDate = dateFilter();
    const out: ListItem[] = [];

    for (const w of completed() ?? []) {
      const performed = displayPerformedAt(w);
      const key = dateKeyISO(performed);
      if (filterDate && key !== filterDate) continue;
      out.push({
        kind: "completed",
        sortAt: new Date(performed).getTime() || 0,
        dateLabel: formatListDate(performed),
        w,
      });
    }

    for (const w of planned() ?? []) {
      // Skip already-completed plans in the unified list (still show under Planned if not done)
      if (
        (w.status === "completed" || w.completedWorkoutId) &&
        tab() === "all"
      ) {
        continue;
      }
      const key = dateKeyISO(w.plannedFor);
      if (filterDate && key !== filterDate) continue;
      const timePart = w.startTime ? `T${w.startTime}:00` : "T12:00:00";
      const sortAt = new Date(`${w.plannedFor}${timePart}`).getTime() || 0;
      const dateLabel = w.startTime
        ? `${formatListDate(w.plannedFor)} · ${w.startTime}`
        : formatListDate(w.plannedFor);
      out.push({
        kind: "planned",
        sortAt,
        dateLabel,
        w,
      });
    }

    const filtered =
      tab() === "completed"
        ? out.filter((i) => i.kind === "completed")
        : tab() === "planned"
          ? out.filter((i) => i.kind === "planned")
          : out;

    return filtered.sort((a, b) => b.sortAt - a.sortAt);
  });

  function setTab(next: Tab) {
    setSearch({ tab: next === "all" ? undefined : next, date: search.date });
  }

  function clearDateFilter() {
    setSearch({ tab: search.tab, date: undefined });
  }

  const emptyTitle = () => {
    if (dateFilter()) return t("NO_WORKOUTS_FOR_DAY");
    if (tab() === "completed") return t("NO_COMPLETED_WORKOUTS");
    if (tab() === "planned") return t("NO_PLANNED_WORKOUTS");
    return t("NO_WORKOUTS_YET");
  };

  const listFrom = () => {
    const q = new URLSearchParams();
    if (tab() !== "all") q.set("tab", tab());
    if (dateFilter()) q.set("date", dateFilter()!);
    const s = q.toString();
    return s ? `/workouts?${s}` : "/workouts";
  };

  return (
    <div class="space-y-5 pb-4">
      <PageHero
        title={t("WORKOUTS")}
        line={dateFilter() ? formatListDate(dateFilter()!) : t("HERO_WORKOUTS_LINE")}
        aside={
          <button
            type="button"
            class="btn-ghost px-2 text-xs"
            onClick={() => navigate("/")}
          >
            ‹ {t("CALENDAR")}
          </button>
        }
      />
      <Show when={dateFilter()}>
        <button
          type="button"
          class="text-[13px] text-[var(--link)]"
          onClick={clearDateFilter}
        >
          {t("CLEAR_FILTER")}
        </button>
      </Show>

      <div class="tg-segmented mb-3" role="tablist" aria-label={t("WORKOUTS")}>
        {(
          [
            ["all", "FILTER_ALL"],
            ["completed", "COMPLETED"],
            ["planned", "PLANNED"],
          ] as const
        ).map(([key, label]) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab() === key}
            onClick={() => setTab(key)}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <div class="mb-3 flex flex-col gap-2">
        <Show when={tab() !== "planned"}>
          <A
            href="/workouts/new/completed"
            class="btn-secondary w-full text-center"
          >
            {t("LOG_WORKOUT")}
          </A>
        </Show>
        <Show when={tab() !== "completed"}>
          <A
            href="/workouts/new/planned"
            class="btn-secondary w-full text-center"
          >
            {t("PLAN_WORKOUT")}
          </A>
        </Show>
        <Show when={tab() === "completed" || tab() === "all"}>
          <A
            href={repeatCompletedHref({ fromLast: true })}
            class="btn-secondary w-full text-center"
          >
            {t("REPEAT_LAST")}
          </A>
        </Show>
      </div>

      <Show when={loading()}>
        <p class="py-8 text-center text-sm text-ink-muted animate-fade-in">
          {t("LOADING")}
        </p>
      </Show>

      <Show when={!loading() && items().length === 0}>
        <div class="empty-state space-y-4">
          <p class="empty-state-title">{emptyTitle()}</p>
          <p class="empty-state-hint">{t("WORKOUTS_EMPTY_HINT")}</p>
          <div class="relative flex w-full max-w-xs flex-col gap-2">
            <A href="/workouts/new/completed" class="btn-primary w-full">
              {t("LOG_WORKOUT")}
            </A>
            <A href="/workouts/new/planned" class="btn-secondary w-full">
              {t("PLAN_WORKOUT")}
            </A>
            <A href="/" class="btn-ghost w-full">
              {t("BACK_TO_CALENDAR")}
            </A>
          </div>
        </div>
      </Show>

      <Show when={!loading() && items().length > 0}>
        <div class="tg-section">
          <For each={items()}>
            {(item) =>
              item.kind === "completed" ? (
                <CompletedCard
                  item={item}
                  from={listFrom()}
                />
              ) : (
                <PlannedCard
                  item={item}
                  from={listFrom()}
                  onStart={() =>
                    navigate(
                      `/workouts/active?fromPlanned=${item.w.id}`,
                      { state: withFrom(listFrom()) },
                    )
                  }
                />
              )
            }
          </For>
        </div>
      </Show>
    </div>
  );
}

function CompletedCard(props: {
  item: Extract<ListItem, { kind: "completed" }>;
  from: string;
}) {
  const w = () => props.item.w;
  const preview = () => workoutPreview(w().exercises);

  return (
    <A
      href={`/workouts/completed/${w().id}`}
      state={withFrom(props.from)}
      class="tg-cell"
    >
      <span class="tg-cell-body">
        <span class="tg-cell-title">{w().title}</span>
        <span class="tg-cell-subtitle">
          {props.item.dateLabel}
          {" · "}
          {w().exercises.length} {t("EXERCISES").toLowerCase()}
          <Show when={formatDuration(w().durationMinutes)}>
            {(d) => <> · {d()}</>}
          </Show>
        </span>
        <For each={preview()}>
          {(line) => <span class="tg-cell-subtitle truncate">{line}</span>}
        </For>
      </span>
      <span class="tg-cell-value">{t("COMPLETED")}</span>
      <svg class="tg-chevron" viewBox="0 0 9 16" aria-hidden="true">
        <path
          d="M1.5 1.5L7.5 8L1.5 14.5"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </A>
  );
}

function plannedStatusBadge(w: PlannedWorkout): string {
  if (w.status === "completed" || w.completedWorkoutId) return t("DONE");
  if (w.status === "missed") return t("MISSED");
  return t("PLANNED");
}

function PlannedCard(props: {
  item: Extract<ListItem, { kind: "planned" }>;
  from: string;
  onStart: () => void;
}) {
  const w = () => props.item.w;
  const preview = () => workoutPreview(w().exercises);
  const open = () =>
    w().status !== "completed" && !w().completedWorkoutId;
  const isMissed = () => w().status === "missed";
  const badge = () => plannedStatusBadge(w());

  return (
    <div>
      <A
        href={`/workouts/planned/${w().id}`}
        state={withFrom(props.from)}
        class="tg-cell"
      >
        <span class="tg-cell-body">
          <span class={`tg-cell-title ${isMissed() ? "text-ink-muted" : ""}`}>
            {w().title}
          </span>
          <span class="tg-cell-subtitle">
            {props.item.dateLabel}
            {" · "}
            {w().exercises.length} {t("EXERCISES").toLowerCase()}
          </span>
          <For each={preview()}>
            {(line) => <span class="tg-cell-subtitle truncate">{line}</span>}
          </For>
        </span>
        <span
          class={`tg-cell-value ${isMissed() ? "text-[var(--destructive)]" : ""}`}
        >
          {badge()}
        </span>
        <svg class="tg-chevron" viewBox="0 0 9 16" aria-hidden="true">
          <path
            d="M1.5 1.5L7.5 8L1.5 14.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </A>
      <Show when={open()}>
        <button
          type="button"
          class="tg-cell"
          onClick={props.onStart}
        >
          <span class="tg-cell-body">
            <span class="tg-cell-title text-[var(--link)]">
              {t("START_WORKOUT_TIMER")}
            </span>
          </span>
        </button>
      </Show>
    </div>
  );
}
